import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'

import { Card } from '@newComponents/Card'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'
import { DocStep } from '../shared/DocStep'

const Bluesky = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Guild integrates with Bluesky so your community can automatically
            post Event announcements to the decentralized social network. When a
            listed Event is created, a post is published to Bluesky with the
            Event name, date and time, and a direct link to the Event page where
            followers can RSVP.
          </Trans>
        </Paragraph>
        <Paragraph size='$5'>
          <Trans>
            Connecting Bluesky gives your Guild its own handle in the format{' '}
            <Paragraph size='$5' fontWeight='$7'>
              @your-guild.guild.host
            </Paragraph>
            . This provides a verified Bluesky presence tied directly to your
            community, making it easy for people to find and follow your Guild
            on the network.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/guilds/guild-settings-bluesky.png'
          alt={t`Guild Bluesky integration settings showing handle configuration and app password fields for connecting to Bluesky`}
          caption={t`Bluesky integration settings with handle and app password configuration`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection id='connecting-bluesky' title={t`Connecting Bluesky`}>
          <Paragraph size='$5'>
            <Trans>
              Only <Link href='/docs/guilds/members'>Organizers</Link> can
              configure the Bluesky integration. To connect Bluesky, follow
              these steps.
            </Trans>
          </Paragraph>
          <DocStep
            number={1}
            title={t`Open Guild Settings`}
            description={t`Navigate to your Guild page and open Settings. You must have the Organizer role to configure integrations.`}
          />
          <DocStep
            number={2}
            title={t`Go to the Bluesky section`}
            description={t`In Settings, find the Bluesky integration section.`}
          />
          <DocStep
            number={3}
            title={t`Enter your Bluesky credentials`}
            description={t`Enter your Bluesky handle and an app password. You can generate an app password from your Bluesky account settings at bsky.app. App passwords are separate from your main password and can be revoked at any time.`}
          />
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='automatic-posting' title={t`Automatic Event Posting`}>
          <Paragraph size='$5'>
            <Trans>
              Once connected, every listed{' '}
              <Link href='/docs/events/overview'>Event</Link> your Guild creates
              is automatically announced on Bluesky. The post includes the Event
              name, date and time, and a link to the Event page on Guild.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              Only listed Events are posted — unlisted Events remain private and
              are not shared on Bluesky. This gives Organizers control over
              which Events get social exposure.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='custom-domain-handle' title={t`Custom Domain Handles`}>
          <Paragraph size='$5'>
            <Trans>
              By default, your Guild&apos;s Bluesky handle uses the guild.host
              subdomain (e.g., @metro-coders.guild.host). If your Guild uses a
              custom domain, you can verify it through DNS to use your own
              domain as your Bluesky handle instead.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              This lets your community appear on Bluesky under a handle your
              audience already recognizes — for example, @meetup.torontojs.com
              instead of @toronto-js.guild.host. Custom domain verification
              involves adding a DNS TXT record, which your domain registrar can
              help you set up.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Bluesky
